import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Divider, List, ListItem, ListItemButton, ListItemDecorator, Typography } from '@mui/joy';
import { supabase } from '../supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { AuthContext } from '../context/AuthContext.jsx';
import NotificationBell from './NotificationBell.jsx';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);

  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (user?.id) {
        const { data, error } = await supabase
          .from('users')
          .select('display_name, user_type')
          .eq('id', user.id)
          .single();

        if (!error) {
          setProfile(data);
        }
      }
    };

    fetchProfile();
  }, [user]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.clear();
    navigate('/');
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: 320,
        maxWidth: '200px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.surface',
        p: 2,
        overflow: 'auto',
        zIndex: 1200,
      }}
    >
      {/* Logo */}
      <Box sx={{ mb: 4, textAlign: 'center', width: '100%' }}>
        <img
          src="/the_grid_logo.png"
          alt="The Grid Logo"
          style={{ maxWidth: '100%', margin: '0 auto' }}
        />
      </Box>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <Typography level="title-md" sx={{ color: 'text.secondary' }}>
            Welcome, {profile?.display_name || 'User'}
          </Typography>
          <NotificationBell />
        </Box>
      </Box>

      {/* Nav Links */}
      <Box sx={{ flexGrow: 1 }}>
        <List
          sx={{
            '& .MuiListItem-root': {
              borderRadius: 'sm',
              mb: 0.5,
              '&:hover': {
                bgcolor: 'success.50',
                transform: 'translateX(4px)',
                transition: 'all 0.2s ease'
              },
              '&.active': {
                bgcolor: 'success.100',
                borderLeft: '3px solid',
                borderColor: 'success.500'
              }
            }
          }}
        >
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/home'}
              onClick={() => navigate('/home')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:view-dashboard-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">Dashboard</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/status-board'}
              onClick={() => navigate('/status-board')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:view-column" />
              </ListItemDecorator>
              <span className="sidebar-label">Status Board</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/permits'}
              onClick={() => navigate('/permits')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:clipboard-text-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">Permit Tracker</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/submittals'}
              onClick={() => navigate('/submittals')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:calendar-clock-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">Submittal Tracker</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/survey-tracker'}
              onClick={() => navigate('/survey-tracker')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:map-marker-radius-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">Survey Tracker</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/gis-projects'}
              onClick={() => navigate('/gis-projects')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:map-marker-multiple-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">GIS Projects</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/client-int-docs'}
              onClick={() => navigate('/client-int-docs')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:file-account-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">Client Docs</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/clients'}
              onClick={() => navigate('/clients')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:account-tie-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">Clients</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/internal-docs'}
              onClick={() => navigate('/internal-docs')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:file-document-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">Internal Docs</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/map'}
              onClick={() => navigate('/map')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:map-search-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">GridMap</span>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              selected={location.pathname === '/my-profile'}
              onClick={() => navigate('/my-profile')}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:account-circle-outline" />
              </ListItemDecorator>
              <span className="sidebar-label">My Profile</span>
            </ListItemButton>
          </ListItem>
          {/* Admin link for Admin or Super Admin users */}
          {['admin', 'super admin', 'super-admin'].includes(profile?.user_type?.toLowerCase()) && (
            <ListItem>
              <ListItemButton
                selected={location.pathname === '/admin'}
                onClick={() => navigate('/admin')}
                sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
              >
                <ListItemDecorator>
                  <Icon icon="mdi:shield-account-outline" />
                </ListItemDecorator>
                <span className="sidebar-label">Admin</span>
              </ListItemButton>
            </ListItem>
          )}
          <ListItem>
            <ListItemButton
              onClick={handleLogout}
              sx={{ 
                '&.Mui-selected': {
                  bgcolor: 'success.100',
                  borderLeft: '3px solid',
                  borderLeftColor: 'success.500',
                  color: 'success.700',
                  '& .MuiListItemDecorator-root': {
                    color: 'success.600'
                  }
                },
                '&:hover': { 
                  bgcolor: 'success.50',
                  transform: 'translateX(2px)',
                  transition: 'all 0.2s ease'
                } 
              }}
            >
              <ListItemDecorator>
                <Icon icon="mdi:logout" />
              </ListItemDecorator>
              <span className="sidebar-label">Logout</span>
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
      <Divider />
      {/* Logout Button */}
      <Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
